import React, { useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";

import { deleteFile } from "api";
import UploadBar from "components/atoms/UploadBar/UploadBar";
import errorToast from "helpers/errorToast";

interface FileAttachmentItemProps {
  currentFile: any;
  onRemove?: () => void;
  hasOtherFilesAttachmentBucket?: boolean;
}

export default function FileAttachmentItem(props: FileAttachmentItemProps) {
  const { currentFile, onRemove, hasOtherFilesAttachmentBucket } = props;
  const [isLoading, setIsLoading] = useState(false);

  const handleRemoveClick = async () => {
    const referenceNumber = hasOtherFilesAttachmentBucket
      ? currentFile.file
      : currentFile.reference_number ?? currentFile.file;
    if (currentFile.progress > 0 && currentFile.abortController) {
      currentFile.abortController.abort();
    } else if (referenceNumber && !currentFile.error) {
      setIsLoading(true);
      try {
        await deleteFile(referenceNumber);
        setIsLoading(false);
      } catch (e: any) {
        setIsLoading(false);
        if (e?.response?.status === 404) {
          // File not found, consider it deleted
          onRemove?.();
          return;
        }
        errorToast(e, "Could not delete file, please try again later");
        return;
      }
    }
    onRemove?.();
  };

  return (
    <Row className="mb-2 align-items-center">
      <Col>
        {currentFile.url ? (
          <a href={currentFile.url} target="_blank" rel="noopener noreferrer">
            {currentFile.filename ?? currentFile.name}
          </a>
        ) : (
          currentFile.name
        )}
      </Col>
      <Col>
        {isLoading ? (
          <div>
            <FontAwesomeIcon icon={faCircleNotch} className="font-16 text-secondary " spin />
          </div>
        ) : (
          <>
            {!!currentFile.progress && !currentFile.error && <UploadBar percentCompleted={currentFile.progress} />}
            {!currentFile.progress && currentFile.error && (
              <div className="text-danger font-b3">
                <div>
                  <FormattedMessage
                    id="new-request.attachments.attachmentFile.uploadFailed"
                    defaultMessage="Upload failed. Please try again"
                  />
                </div>
                <div>
                  {currentFile?.errorMessage ? (
                    currentFile.errorMessage
                  ) : (
                    <FormattedMessage
                      id="new-request.attachments.attachmentFile.maxUploadSize"
                      defaultMessage="(max filesize 15mb)"
                    />
                  )}
                </div>
              </div>
            )}
            {!currentFile.progress && !currentFile.error && currentFile.url && (
              <span className="text-nowrap">
                <FormattedMessage id="new-request.otherAttachment.fileUploaded" defaultMessage="File uploaded" />
              </span>
            )}
          </>
        )}
      </Col>
      <Col className="d-flex justify-content-center">
        <Button type="button" variant="link" className="remove-btn p-0" onClick={handleRemoveClick} disabled={isLoading}>
          <FormattedMessage id="general.remove" defaultMessage="Remove" />
        </Button>
      </Col>
    </Row>
  );
}

FileAttachmentItem.defaultProps = {
  hasOtherFilesAttachmentBucket: false,
  onRemove: undefined,
};
